/* global __CLIENT__, FM */ //eslint-disable-line
/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as queryString from 'query-string';
import { StatsigProvider } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import '../../assets/global.scss';
import '../../assets/android-banner.scss';
import Header from './components/Header';
import Footer from './components/Footer';
import Polling from './components/Polling';
import PopupContainer from './components/PopupContainer';
import withTracker from './components/hocs/withTracker';
import { useStatsig } from './components/hooks/useStatsig';
import { useReauthListener } from './components/hooks/useReauthListener';
import ProgressBar from './components/ProgressBar';
import StatsigReadyLatch from './components/StatsigReadyLatch';
import { getUserAndTypes } from './actions/combined-actions';
import { setTrackAnonymousUsers } from './actions/statsig-actions';
import { addFreshchatRestoreId } from './api';
import Notifications from './components/Notifications';
import LoginPopUpWrapper from './components/PopUps/LoginPopUpWrapper';
import { closeLoginPopUp } from './actions/login-popup-actions';
import ErrorBoundary from './components/ErrorBoundary';

import MenuNew from './components/Menu';
import { getApiAxiosInstance } from './utils/network';
import ImpersonateMemberNotification from './components/ImpersonateMember/Notification';
import { setAuthHeader } from './actions';
import { getFeedPreferences } from './actions/settings-actions';
import { shouldDisplayActivateUtrNotification } from './utils/activateUtr';
import { useBrazeIdentityReconcile } from './components/hooks/useBrazeIdentityReconcile';
import { getDefaultCustomFields } from './utils/statsigUserFields';
import { getStatsigEnvironmentTier, isProductionConfigSource } from './utils/statsig';
import { syncDatadogUser } from './utils/errorReporting';
import {
    resolveAnalyticsEnvironment,
    buildUserIdentifiedEvent,
    getFreshchatRestoreId,
    buildAppWrapperClassName,
} from './utils/appContentHelpers';

import config from '../config';

const routePropType = PropTypes.shape({
    path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    exact: PropTypes.bool,
    component: PropTypes.elementType,
});

function AppContent(props) {
    const [showHeader, setShowHeader] = useState(true);
    const { checkGate, featureGates } = useStatsig();

    const header = useSelector((state) => state.header);
    const loginPopUp = useSelector((state) => state.loginPopUp);
    const user = useSelector((state) => state.auth?.user);
    const feedPreferences = useSelector((state) => state.auth?.feedPreferences);
    const isFeedPreferencesLoading = useSelector((state) => state.auth?.isFeedPreferencesLoading);
    const activateUtr = useSelector((state) => state.activateUtr);

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    // web_segment_track_anonymous_users controls the full pre-ENG-2641 Segment behavior: beyond
    // re-enabling Segment tracking for anonymous visitors, it also re-applies each route's
    // analyticsCategory on page() for ALL visitors (authenticated included), so the gate name
    // understates its reach — keep the Statsig gate description in sync with this. The gate is read
    // here (inside StatsigProvider) and mirrored into Redux so withTracker — which sits above the
    // provider and can't read a gate — can consume it.
    const trackAnonymousUsers = checkGate(featureGates.SEGMENT_TRACK_ANONYMOUS_USERS);
    useEffect(() => {
        dispatch(setTrackAnonymousUsers(trackAnonymousUsers));
    }, [trackAnonymousUsers, dispatch]);

    useReauthListener();
    useBrazeIdentityReconcile();

    const getRoute = (location) => {
        const matchedRoutes = matchRoutes(props.route.routes, location.pathname);
        return matchedRoutes[0]?.route;
    };

    const destroyFreshChatWidget = () => {
        if (window?.fcWidget !== undefined) {
            window.fcWidget.destroy();
        }
    };

    const initFreshchatWidget = async (user) => {
        const isFreshchatEnabled = checkGate(featureGates.FRESHCHAT_ENABLED);

        if (!isFreshchatEnabled || !window?.fcWidget) {
            return;
        }

        window.fcWidget.init({
            token: config.freshChatToken,
            host: 'https://wchat.freshchat.com',
            config: { showFAQOnOpen: false },
            externalId: user?.id,
            restoreId: user?.freshchatRestoreId || null,
        });

        const setUserProperties = () => {
            if (user && window.fcWidget.user) {
                window.fcWidget.user.setProperties({
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    subscriptionType: user.subscriptionType,
                });
            }
        };

        const handleUserCreated = (createdResponse) => {
            const restoreId = getFreshchatRestoreId(createdResponse);
            if (restoreId) {
                addFreshchatRestoreId(getApiAxiosInstance(), restoreId);
            }
        };

        try {
            const response = await window.fcWidget.user?.get();
            if (response?.status === 200) {
                setUserProperties();
            }
        } catch {
            setUserProperties();
            window.fcWidget.on('user:created', handleUserCreated);
        }
    };

    useEffect(() => {
        const parsed = queryString.parse(window?.location?.search);
        const { jwt } = parsed;
        if (jwt) {
            setAuthHeader({ jwt });
        }

        // Clean up URL hash after cookie consent modal closes
        const listenerName = 'hashchange';
        const handleHashChange = () => {
            // When the hash is empty (just "#" with nothing after), clean up the URL
            if (window.location.hash === '' || window.location.hash === '#') {
                window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
            }
        };

        window.addEventListener(listenerName, handleHashChange);

        return () => {
            window.removeEventListener(listenerName, handleHashChange);
        };
    }, []);

    useEffect(() => {
        const configSource = process.env.CONFIG_SOURCE;
        const currentRoute = getRoute(location);
        const showWidget = currentRoute?.supportWidget ? !currentRoute.supportWidget.disabled : true;
        const headerDisabled = !!currentRoute?.header?.disabled;
        const isFreshChatWidgetInitialized = window?.fcWidget?.isInitialized();
        const shouldFetchFeedPreferences = !feedPreferences && !isFeedPreferencesLoading;
        const environment = resolveAnalyticsEnvironment(configSource);

        if (window?.dataLayer) {
            window.dataLayer.push(buildUserIdentifiedEvent(user, environment));
        }
        if (isProductionConfigSource(configSource)) {
            syncDatadogUser(user);
        }

        if (user) {
            if (showWidget && !isFreshChatWidgetInitialized) {
                initFreshchatWidget(user);
            } else if (!showWidget) {
                destroyFreshChatWidget();
            }
            if (shouldFetchFeedPreferences) {
                dispatch(getFeedPreferences());
            }
        } else {
            destroyFreshChatWidget();
        }
        setShowHeader(!headerDisabled);
    }, [user, feedPreferences, location?.pathname]);

    if (__CLIENT__ && user && window?.FM && typeof window?.FM?.identify === 'function') {
        FM.identify(user?.email);
    }

    const isEventCreation = location.pathname.includes('/events/create');
    const isEventEditPage = location.pathname.includes('/events') && location.pathname.includes('/edit');
    const currentRoute = getRoute(location);
    const { mobileWebView } = currentRoute;
    const showExpandedNav = !!user || user?.onboardingCompleted || !user?.onboardingRequired;
    const isImpersonating = user?.impersonationOriginatingMemberId && (user?.impersonationOriginatingMemberId !== user?.id);
    const activatingUtr = shouldDisplayActivateUtrNotification(activateUtr, location.pathname);
    const env = (process.env.CONFIG_SOURCE || 'local').toLowerCase();

    const closeLoginModal = () => {
        loginPopUp.onCloseClick?.();
        dispatch(closeLoginPopUp());
    };

    const expandedNavTitleClass = header.type === 'title' ? 'title' : '';
    const wrapperClassName = buildAppWrapperClassName({
        env,
        showExpandedNav,
        expandedNavTitleClass,
        loggedOut: !user,
        noHeader: !showHeader || header.disabled,
        customHeader: header.custom,
        transparentHeader: header.style === 'transparent',
        activatingUtr,
        isImpersonating,
        mobileWebView,
    });

    return (
        <div
            id="myutr-app-wrapper"
            className={wrapperClassName}
        >
            {isImpersonating && (
                <ImpersonateMemberNotification
                    user={user}
                    history={history}
                />
            )}
            <ProgressBar />
            <Header
                isImpersonating={isImpersonating}
                themeV2={true}
            />
            <MenuNew />
            <ErrorBoundary main>
                <div id="myutr-app-body">
                    {renderRoutes(props.route.routes)}
                </div>
            </ErrorBoundary>
            {!isEventCreation && !isEventEditPage && (currentRoute.footer ? !currentRoute.footer.disabled : true) && <Footer />}
            <Polling />
            <Notifications currentRoute={currentRoute} />
            {loginPopUp.showPopUp && (
                <LoginPopUpWrapper
                    {...props}
                    onCloseClick={closeLoginModal}
                    userParamsNeeded={loginPopUp.userParamsNeeded}
                    performAction={loginPopUp.performAction}
                    extraFunctionCalls={loginPopUp.extraFunctionCalls}
                    onboardFlow={loginPopUp.onboardFlow}
                    type={loginPopUp.type}
                    titles={loginPopUp.titles}
                    screen={loginPopUp?.screen}
                    showCloseIcon={loginPopUp?.showCloseIcon}
                    closeOnClickOutside={true}
                />
            )}
            <PopupContainer />
        </div>
    );
}

AppContent.propTypes = {
    route: PropTypes.shape({
        routes: PropTypes.arrayOf(routePropType).isRequired,
    }).isRequired,
};

function App(props) {
    const user = useSelector((state) => state.auth?.user);
    const isWindowUndefined = typeof window === 'undefined';
    const environmentTier = getStatsigEnvironmentTier(process.env.CONFIG_SOURCE);

    if (!isWindowUndefined) {
        const requiredKeys = ['brazeApiKey', 'segmentKey', 'statsigKey', 'stripeKey', 'stripeClientId', 'utrStripeAccountId', 'googleApiKey'];
        const keyStatus = requiredKeys.reduce((acc, key) => ({ ...acc, [key]: !!config[key] }), {});
        console.log('[config] required keys present:', keyStatus);
    }

    return (
        <StatsigProvider
            sdkKey={config.statsigKey}
            user={{
                userID: user?.id || null,
                email: user?.email || null,
                custom: getDefaultCustomFields(user),
            }}
            options={{
                networkConfig: {
                    preventAllNetworkTraffic: isWindowUndefined,
                },
                disableStorage: isWindowUndefined,
                environment: { tier: environmentTier },
            }}
        >
            <StatsigReadyLatch>
                <AppContent {...props} />
            </StatsigReadyLatch>
        </StatsigProvider>
    );
}

App.propTypes = {
    route: PropTypes.shape({
        routes: PropTypes.arrayOf(routePropType).isRequired,
    }).isRequired,
};

export default {
    component: withTracker(App),
    loadData: ({ dispatch }) => dispatch(getUserAndTypes()),
};
